import React, { useEffect, useState } from "react";
import "../assets/EndRemoteDebug.css";
import "../assets/AllTabsCommon.css";
import "../assets/ViewRemoteDebugSession.css";
import { fetchData, patchData } from "../api/restapi";
import Popup from "../popups/popup";

function ScheduledSessionsTab({ userInfo, role, prefilledSystemId }) {
  const [scheduledSessions, setScheduledSessions] = useState([]);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [expandedRowData, setExpandedRowData] = useState(null);
  const [confirmingCancelId, setConfirmingCancelId] = useState(null);
  const [cancelingSessionId, setCancelingSessionId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [systemIdSearchTerm, setSystemIdSearchTerm] = useState(prefilledSystemId || "");
  const [collectorNames, setCollectorNames] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Helper function to map scheduling mode for display
  const getSchedulingModeDisplay = (mode) => {
    switch(mode) {
      case "FIXED":
        return "At";
      case "FLEXIBLE":
        return "By";
      default:
        return mode;
    }
  };

  const tableColumns = [
    { key: "systemId", label: "System ID" },
    { key: "sessionId", label: "Session ID" },
    { key: "scheduledTime", label: "Scheduled Time", tooltip: "All times are displayed in your local time zone." },
    { key: "scheduledBy", label: "Created User" },
    { key: "schedulingMode", label: "Scheduling Mode" },
    { key: "action", label: "Action", isAction: true }
  ];

  useEffect(() => {
    const fetchScheduledSessions = async () => {
      try {
        const data = await fetchData(`/api/v1/scheduled-sessions?user=${userInfo}&userRole=${role}`);
        console.log("API Response:", data);
        if (data.scheduledRdSessions) {
          const sessionsWithFormattedTime = data.scheduledRdSessions.map(session => {
            const scheduledDate = new Date(session.scheduledTime);
            const formattedScheduledTime = scheduledDate.toLocaleString(undefined, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false
            }).replace(",", "");
            return {
              ...session,
              sessionId: session.scheduledSessionId,
              scheduledTimeFormatted: formattedScheduledTime
            };
          });
          setScheduledSessions(sessionsWithFormattedTime);
        }
      } catch (err) {
        console.error("Error fetching scheduled sessions:", err);
      }
    };

    const fetchCollectorNames = async () => {
      try {
        const data = await fetchData("/api/v1/diagnostic-data");
        if (data.statusCode === 200) {
          const namesMap = data.collectorIds.reduce((acc, collector) => {
            acc[collector.id] = collector.name;
            return acc;
          }, {});
          setCollectorNames(namesMap);
        }
      } catch (err) {
        console.error("Error fetching collector names:", err);
      }
    };

    fetchScheduledSessions();
    fetchCollectorNames();
  }, [userInfo, role]);

  useEffect(() => {
    if (prefilledSystemId) {
      setSystemIdSearchTerm(prefilledSystemId);
    }
  }, [prefilledSystemId]);

  const handleCancelSession = (session) => {
    if (confirmingCancelId === session.scheduledSessionId) {
      setConfirmingCancelId(null);
    } else {
      setConfirmingCancelId(session.scheduledSessionId);
    }
  };

  const handleConfirmCancel = async (session) => {
    setConfirmingCancelId(null);
    setCancelingSessionId(session.scheduledSessionId);

    try {
      const response = await patchData(`/api/v1/scheduled-sessions/${session.scheduledSessionId}/cancel`, {});
      if (response.statusCode === 200) {
        setTimeout(() => {
          setScheduledSessions(scheduledSessions.filter(s => s.scheduledSessionId !== session.scheduledSessionId));
          setCancelingSessionId(null);
        }, 2000);
      } else {
        setErrorMessage(response.errorMessage || `Failed to cancel session ${session.scheduledSessionId}.`);
        setCancelingSessionId(null);
      }
    } catch (error) {
      setErrorMessage(`Failed to cancel session ${session.scheduledSessionId}: ${error.message}`);
      setCancelingSessionId(null);
    }
  };

  const handleViewDetails = async (session) => {
    if (expandedRowId === session.scheduledSessionId) {
      setExpandedRowId(null);
      setExpandedRowData(null);
      return;
    }

    try {
      let diagnosticData = [];
      if (session.wbcCollectorConfigs) {
        try {
          const configs = JSON.parse(session.wbcCollectorConfigs);
          diagnosticData = configs.map(config => ({
            collectorId: config.collectorId,
            commandName: config.commandName
          }));
        } catch (parseError) {
          console.error("Error parsing wbcCollectorConfigs:", parseError);
        }
      }

      const newExpandedRowData = {
        ...session,
        diagnosticData: diagnosticData
      };

      setExpandedRowId(session.scheduledSessionId);
      setExpandedRowData(newExpandedRowData);
    } catch (error) {
      setErrorMessage(`Failed to fetch details: ${error.message}`);
    }
  };

  const closePopup = () => {
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const filteredSessions = scheduledSessions.filter((session) => {
    return session.systemId.toLowerCase().includes(systemIdSearchTerm.toLowerCase());
  });

  const totalPages = Math.ceil(filteredSessions.length / rowsPerPage);
  const paginatedSessions = filteredSessions.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const renderPageNumbers = () => {
    const maxPagesToShow = 6;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={currentPage === i ? "active" : ""}
        >
          {i}
        </button>
      );
    }

    return (
      <>
        <button onClick={() => handlePageChange(1)}>{'<<'}</button>
        {pageNumbers}
        <button onClick={() => handlePageChange(totalPages)}>{'>>'}</button>
      </>
    );
  };

  return (
    <div className="data-table-tab-container">
      <div className="tab-heading">
        <h1>All Scheduled Sessions</h1>
      </div>
      <div className="search-container">
        <input
          type="text"
          placeholder="🔍 Search by System ID..."
          value={systemIdSearchTerm}
          onChange={(e) => setSystemIdSearchTerm(e.target.value)}
        />
      </div>
      <table className="data-table">
        <thead>
          <tr>
            {tableColumns.map((col) => (
              <th key={col.key}>
                {col.label}
                {col.tooltip && (
                  <span className="tooltip-icon" data-tooltip={col.tooltip}>ℹ️</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedSessions.map((session) => (
            <React.Fragment key={session.scheduledSessionId}>
              <tr className={expandedRowId === session.scheduledSessionId ? 'expanded-row' : ''}>
                {tableColumns.map((col) => {
                  if (col.key === "action") {
                    return (
                      <td key={col.key}>
                        {session.status === "SCHEDULED" ? (
                          <button
                            className="add-criteria-button"
                            onClick={() => handleCancelSession(session)}
                            disabled={cancelingSessionId === session.scheduledSessionId}
                          >
                            {cancelingSessionId === session.scheduledSessionId
                              ? "Canceling..."
                              : confirmingCancelId === session.scheduledSessionId
                              ? "Cancel"
                              : "Cancel"}
                          </button>
                        ) : (
                          <span>-</span>
                        )}
                      </td>
                    );
                  }

                  if (col.key === "sessionId") {
                    return (
                      <td key={col.key} onClick={() => handleViewDetails(session)} className="clickable-session-id">
                        {session.scheduledSessionId}
                      </td>
                    );
                  }

                  if (col.key === "scheduledTime") {
                    return <td key={col.key}>{session.scheduledTimeFormatted}</td>;
                  }

                  if (col.key === "schedulingMode") {
                    return <td key={col.key}>{getSchedulingModeDisplay(session.schedulingMode)}</td>;
                  }

                  return <td key={col.key}>{session[col.key] || ""}</td>;
                })}
              </tr>
              {expandedRowId === session.scheduledSessionId && expandedRowData && (
                <tr className="expanded-details-row">
                  <td colSpan={tableColumns.length}>
                    <div className="expanded-row-content">
                      <div className="expanded-details-cards expanded-details-row-1">
                        <div className="expanded-detail-card">
                          <h4>
                            <span className="card-header-icon info-icon"></span>
                            Session Information
                          </h4>
                          <div className="expanded-detail-card-content">
                            <div className="session-info-item">
                              <span className="info-label">Duration:</span>
                              <span className="info-value">{expandedRowData.sessionTimeInMins} mins</span>
                            </div>
                            <div className="session-info-item">
                              <span className="info-label">Scheduled Time:</span>
                              <span className="info-value">{expandedRowData.scheduledTimeFormatted}</span>
                            </div>
                            <div className="session-info-item">
                              <span className="info-label">Scheduling Mode:</span>
                              <span className="info-value">{getSchedulingModeDisplay(expandedRowData.schedulingMode)}</span>
                            </div>
                            <div className="session-info-item">
                              <span className="info-label">Created On:</span>
                              <span className="info-value">
                                {new Date(expandedRowData.createdOn).toLocaleString(undefined, {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                  hour12: false
                                }).replace(",", "")}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="expanded-detail-card">
                          <h4>
                            <span className="card-header-icon diagnostic-icon"></span>
                            Diagnostic Data
                          </h4>
                          <div className="expanded-detail-card-content">
                            <table className="details-table">
                              <thead>
                                <tr>
                                  <th>Diagnostic Data</th>
                                  <th>Command Name</th>
                                </tr>
                              </thead>
                              <tbody>
                                {expandedRowData.diagnosticData && expandedRowData.diagnosticData.length > 0 ? (
                                  expandedRowData.diagnosticData.map((data, index) => (
                                    <tr key={index}>
                                      <td>{collectorNames[data.collectorId] || data.collectorId}</td>
                                      <td>{data.commandName}</td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="2">No diagnostic data available.</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {confirmingCancelId === session.scheduledSessionId && (
                <tr className="confirmation-row">
                  <td colSpan={tableColumns.length}>
                    <div className="confirmation-content">
                      <div className="confirmation-message">
                        <span className="warning-icon">⚠️</span>
                        <span className="confirmation-text">
                          Are you sure you want to cancel scheduled session <strong>{session.scheduledSessionId}</strong>?
                        </span>
                      </div>
                      <div className="confirmation-buttons">
                        <button 
                          className="cancel-button"
                          onClick={() => setConfirmingCancelId(null)}
                        >
                          Cancel
                        </button>
                        <button 
                          className="confirm-button"
                          onClick={() => handleConfirmCancel(session)}
                        >
                          Confirm Cancel Session
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {cancelingSessionId === session.scheduledSessionId && (
                <tr className="success-row">
                  <td colSpan={tableColumns.length}>
                    <div className="success-content">
                      <span className="success-icon">✅</span>
                      <span className="success-text">
                        Scheduled session <strong>{session.scheduledSessionId}</strong> canceled successfully
                      </span>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <div className="pagination-controls">{renderPageNumbers()}</div>
      {successMessage && (
        <Popup message={successMessage} onClose={closePopup} />
      )}
      {errorMessage && (
        <Popup message={errorMessage} onClose={closePopup} />
      )}
    </div>
  );
}

export default ScheduledSessionsTab;
